import { useMemo, useState } from 'react';
import { ActionIcon, Divider, Flex, Text } from '@mantine/core';
import { DetailsChip } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DetailsChip.tsx';
import { ClassifierType } from '@dealstack/shared/lib/generated';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import {
	useClassifiersForInvestor,
	useMipClassifiersList,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/hooks/useMipClassifiers.ts';
import { ManageInvestorMipClassifiersDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/ManageInvestorMipClassifiersDrawer/ManageInvestorMipClassifiersDrawer.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	investorId: string;
	investorName: string;
}

export const InvestorCohorts = ({ investorId, investorName }: Props) => {
	const { mipId } = useRequiredParams('mipId');
	const tMipManage = useTranslationMipManage();
	const [drawerOpened, setDrawerOpened] = useState(false);

	const { hasPermission: canEdit } = usePermissionRequest({
		resourceType: ResourceType.PROGRAMME,
		resourceId: mipId,
		permissionRole: PermissionRole.EDITOR,
	});

	const {
		data: assignedMipClassifiers = [],
		isLoading: isLoadingAssigned,
		refetch: refetchAssignedMipClassifiers,
	} = useClassifiersForInvestor({ investorId });

	const { data: allActiveMipClassifiers = [] } = useMipClassifiersList({
		mipId,
		isArchived: false,
		classifiedType: ClassifierType.INVESTOR_COHORT,
	});

	const availableMipClassifiersToAdd = useMemo(() => {
		const assignedIds = new Set(assignedMipClassifiers.map((c) => c.id));
		return allActiveMipClassifiers.filter((c) => !assignedIds.has(c.id));
	}, [allActiveMipClassifiers, assignedMipClassifiers]);

	if (assignedMipClassifiers.length === 0 && !canEdit) {
		return null;
	}

	return (
		<Flex gap="0.25rem" align="center">
			<Divider orientation="vertical" color="gray.2" w={1} h="1.25rem" mr="0.25rem" />

			<Text c="gray.9">{tMipManage('investors.details.cohorts_label')}</Text>

			{assignedMipClassifiers.map((mipClassifier) => (
				<DetailsChip
					key={mipClassifier.id}
					label={mipClassifier.name}
					dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_COHORT_BADGE}
				/>
			))}

			{canEdit && (
				<ActionIcon
					variant="transparent"
					size="sm"
					c="dark.8"
					disabled={isLoadingAssigned}
					onClick={() => setDrawerOpened(true)}
					data-testid={
						TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_MANAGE_COHORTS_BUTTON
					}
				>
					<Icon variant="settings" size="md" />
				</ActionIcon>
			)}

			<ManageInvestorMipClassifiersDrawer
				investorId={investorId}
				investorName={investorName}
				availableMipClassifiers={availableMipClassifiersToAdd}
				hasPermission={canEdit}
				opened={drawerOpened}
				onClose={() => setDrawerOpened(false)}
				onSuccess={refetchAssignedMipClassifiers}
			/>
		</Flex>
	);
};
